import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { ApiService } from './api.service';
import { SelectOption } from '../../shared/components/searchable-select/searchable-select.component';

@Injectable({ providedIn: 'root' })
export class LookupService {
  constructor(private api: ApiService) {}

  searchCategories(term: string, limit = 50): Observable<SelectOption[]> {
    const params: Record<string, string | number> = {
      limit,
      page: 1,
      is_active: 1
    };
    if (term.trim()) {
      params['search'] = term.trim();
    }
    return this.api.get<{ success: boolean; data: any[] | { data: any[] } }>(
      'categories/category_list.php',
      params
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) return [];
        const rows = Array.isArray(response.data)
          ? response.data
          : (response.data as any).data || [];
        return rows
          .filter((cat: any) => cat.is_active == 1 || cat.is_active === '1')
          .map((cat: any) => ({
            value: parseInt(cat.id, 10),
            label: cat.name
          }));
      })
    );
  }

  getCategoryOption(categoryId: number): Observable<SelectOption | null> {
    if (!categoryId) return of(null);
    return this.api.get<{ success: boolean; data: any[] | { data: any[] } }>(
      'categories/category_list.php',
      { id: categoryId, limit: 1, is_active: -1 }
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) return null;
        const rows = Array.isArray(response.data)
          ? response.data
          : (response.data as any).data || [];
        const cat = rows[0];
        if (!cat) return null;
        return { value: parseInt(cat.id, 10), label: cat.name };
      })
    );
  }

  searchProducts(term: string, limit = 50): Observable<any[]> {
    const q = term.trim();
    if (!q) {
      return this.api.get<{ success: boolean; data: any[] | { data: any[] } }>(
        'products/product_list.php',
        { limit, page: 1, is_active: 1 }
      ).pipe(
        map((response) => {
          if (!response.success || !response.data) return [];
          return Array.isArray(response.data)
            ? response.data
            : (response.data as any).data || [];
        })
      );
    }
    return this.api.get<{ success: boolean; data: any[] }>(
      'products/product_search.php',
      { q }
    ).pipe(map((response) => (response.success && response.data ? response.data : [])));
  }

  searchVendors(term: string, limit = 50): Observable<SelectOption[]> {
    const params: Record<string, string | number> = {
      limit,
      is_active: 1
    };
    if (term.trim()) {
      params['search'] = term.trim();
    }
    return this.api.get<{ success: boolean; data: any[] }>(
      'vendors/vendor_list.php',
      params
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) return [];
        return (response.data || []).map((v: any) => ({
          value: parseInt(v.id, 10),
          label: v.name
        }));
      })
    );
  }

  getVendorOption(vendorId: number): Observable<SelectOption | null> {
    if (!vendorId) return of(null);
    return this.api.get<{ success: boolean; data: any[] }>(
      'vendors/vendor_list.php',
      { is_active: -1, limit: 500 }
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) return null;
        const vendor = (response.data || []).find((v: any) => parseInt(v.id, 10) === vendorId);
        if (!vendor) return null;
        return { value: parseInt(vendor.id, 10), label: vendor.name };
      })
    );
  }

  searchUnitTypes(term: string, limit = 100): Observable<SelectOption[]> {
    const params: Record<string, string | number> = {
      is_active: 1
    };
    if (term.trim()) {
      params['search'] = term.trim();
    }
    return this.api.get<{ success: boolean; data: any[] }>(
      'units/unit_list.php',
      params
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) return [];
        return (response.data || [])
          .slice(0, limit)
          .map((u: any) => ({
            value: parseInt(u.id, 10),
            label: `${u.name} (${u.code})`
          }));
      })
    );
  }
}
